/* eslint-disable no-alert */
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { Box } from '@mui/material';
// hooks
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import PieSocket from 'piesocket-js';
import { refreshThreadMessages, resetChatData } from 'src/redux/slices/whatsapp';
import { setPersistedWpNotifications, shownWpToastIds } from 'src/redux/slices/liveNotificationSlice';
import { fetchMergeTagKeys, fetchMergeTagValueForPractice, fetchMergeTagValueForUser, fetchPracticeUsers } from 'src/redux/slices/mergeTags';
import { getAllTagItem } from 'src/redux/slices/tagsCreate';
import { selectLeads } from 'src/redux/slices/lead';
import { applyMessageEdited, applyMessagePinned, applyMessageReaction, applyMessageRevoked, applyMessageStarred, applyMessageUnpinned, applyMessageUnstarred, handleAddWhatsappNotificationCount, initializeWhatsapp, refreshThreadAndMessageInfo, updateSubChannel } from 'src/redux/slices/whatsappV2';
import { requestAndRegisterFCMToken, listenForForegroundMessages } from 'src/fcmService';
import { prefetchGoogleReviewsOnDashboardLoad } from 'src/redux/slices/reviewStatsCache';
import { getPracticeTablePreferences } from 'src/redux/slices/tablePreferences';
import { useNotifications } from '../../sections/notifications/NotificationProvider';
import { PATH_DASHBOARD } from '../../routes/paths';
import { useResponsive } from '../../hooks';
import { useSettingsContext } from '../../components/settings/context';
import Main from './Main';
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import NavMini from './navbar/NavMini';
import EnableNotificationsDialog from './popups/EnableNotificationsDialog';
import CommandPalette from '../../components/CommandPalette';
import CreateLeadDialogWrapper from '../../components/CreateLeadDialogWrapper';
import TaskUndoSnackbarHost from '../../sections/tasks/TaskUndoSnackbarHost';

function showCustomNotification() {
    try {
        // Check for notification permission first
        if (Notification.permission === "granted") {
            // this is triggered from service worker for system notification
        } else if (Notification.permission === "default") {
            // If it's not denied, we can ask for permission
            Notification.requestPermission().then(permission => {
                if (permission === "granted") {
                    new Notification("Permission Granted!", {
                        body: "You will now recieve notifications like this!",
                        icon: "/favicon/android-chrome-512x512.png"
                    });
                }
            });
        }
    } catch (error) {
        console.error("Error in OS Notif", error)
    }
}

export default function DashboardLayout() {
    const { themeLayout } = useSettingsContext();
    const dispatch = useDispatch();
    const { currentPractice, whatsappJwtTokens, businessIdToPhoneMap } = useSelector(
        (state) => ({
            jwtToken: state?.whatsApp?.jwtToken,
            practiceMetaData: state?.whatsApp?.practiceMetaData,
            currentPractice: state.practiceTreatmentList?.practiceId,
            whatsappJwtTokens: state.whatsappV2?.whatsappJwtTokens,
            businessIdToPhoneMap: state.whatsappV2?.businessIdToPhoneMap,
        }),
        shallowEqual
    );
    const { showNotification } = useNotifications();
    const showNotificationRef = React.useRef(showNotification);
    showNotificationRef.current = showNotification;
    const isDesktop = useResponsive('up', 'lg');
    const [open, setOpen] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const horizontalLayout = themeLayout === 'horizontal';
    const isNavMini = themeLayout === 'mini';

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const renderNavVertical = <NavbarVertical openNav={open} onCloseNav={handleClose} />;

    React.useEffect(() => {
        const jwtArray = Object.keys(whatsappJwtTokens ?? {}).flatMap(businessId =>
            Object.keys(whatsappJwtTokens?.[businessId] ?? {}).map(phoneId => {
                const phoneNumber = businessIdToPhoneMap?.[businessId]?.filter(phoneOb => `${phoneOb?.id}` === `${phoneId}`)?.[0]?.phone_number_id ?? '';
                return ({
                    phoneId,
                    businessId,
                    jwtToken: whatsappJwtTokens?.[businessId]?.[phoneId] ?? '',
                    roomId: `${currentPractice}_${phoneNumber}`,
                    phoneNumber
                })
            })
        );

        jwtArray?.forEach(jwtObj => {
            if (jwtObj?.jwtToken && jwtObj?.phoneNumber && jwtObj?.roomId) {
                const pieSocketConn = new PieSocket({
                    clusterId: import.meta.env.VITE_REACT_APP_SOCKET_CLUSTER_KEY || '',
                    apiKey: import.meta.env.VITE_REACT_APP_SOCKET_API_KEY || '',
                    jwt: jwtObj.jwtToken,
                });
                pieSocketConn.subscribe(jwtObj.roomId).then(channel => {
                    const listener = (data) => {
                        if (jwtObj.phoneNumber !== data?.from) {
                            dispatch(setPersistedWpNotifications(data));
                            dispatch(handleAddWhatsappNotificationCount());

                            // Show WhatsApp toast from the phone-specific channel.
                            // This is the reliable path — the platform _notifications channel
                            // may not always deliver WhatsApp events.
                            const wpMsgId = data?.id || data?.wamid || '';
                            if (!wpMsgId || !shownWpToastIds.has(wpMsgId)) {
                                if (wpMsgId) shownWpToastIds.add(wpMsgId);
                                const threadId = data?.thread || data?.threadId || data?.thread_id;
                                showNotificationRef.current({
                                    id: wpMsgId || `wp_phone_${Date.now()}`,
                                    verb: 'whatsapp_message_received',
                                    description: data?.name
                                        ? `WhatsApp from ${data.name}: ${data.body || ''}`
                                        : (data?.body || 'New WhatsApp message'),
                                    priority: 'normal',
                                    requires_action: true,
                                    action_label: 'Open Chat',
                                    action_link: threadId && currentPractice
                                        ? PATH_DASHBOARD.whatsapp.thread(currentPractice, threadId)
                                        : undefined,
                                    data: { threadId },
                                });
                            }
                        }
                        dispatch(refreshThreadAndMessageInfo(data?.thread));
                        dispatch(refreshThreadMessages(data?.thread || -1, true, true));
                    };

                    channel.listen('new-whatsapp-message', listener);

                    const reactionListener = (data) => {
                        const messageId = data?.message_id ?? data?.id;
                        if (messageId == null) return;
                        dispatch(applyMessageReaction({
                            messageId,
                            emoji: data?.emoji ?? null,
                            removed: Boolean(data?.removed),
                            byUser: data?.by_user ?? null,
                        }));
                    };
                    channel.listen('message-reaction', reactionListener);

                    const revokedListener = (data) => {
                        const messageId = data?.message_id ?? data?.id;
                        if (messageId == null) return;
                        dispatch(applyMessageRevoked({ messageId }));
                    };
                    channel.listen('message-revoked', revokedListener);

                    // Track B — Star + Pin live updates. Each handler is a
                    // tiny dispatch; the events are practice-wide so any
                    // operator's action propagates to every connected
                    // client. message_id / thread_id arrive flat on data.
                    channel.listen('message-starred', (data) => {
                        dispatch(applyMessageStarred({
                            messageId: data?.message_id,
                            starredBy: data?.by_user,
                        }));
                    });
                    channel.listen('message-unstarred', (data) => {
                        dispatch(applyMessageUnstarred({
                            messageId: data?.message_id,
                        }));
                    });
                    channel.listen('message-pinned', (data) => {
                        dispatch(applyMessagePinned({
                            messageId: data?.message_id,
                            threadId: data?.thread_id,
                            pinnedMessage: { id: data?.message_id },
                            pinnedAt: new Date().toISOString(),
                        }));
                    });
                    channel.listen('message-unpinned', (data) => {
                        dispatch(applyMessageUnpinned({
                            threadId: data?.thread_id,
                        }));
                    });

                    // Track C / F6: a peer operator edited an outbound message.
                    // Apply the body swap surgically — no full thread refetch
                    // needed because the wamid + thread_id arrive in the
                    // payload. Mirrors the Track A reaction listener pattern.
                    const editedListener = (data) => {
                        const messageId = data?.message_id;
                        const newBody = data?.new_body;
                        if (!messageId || newBody == null) return;
                        dispatch(applyMessageEdited({
                            messageId,
                            newBody,
                            editedAt: data?.edited_at,
                        }));
                    };
                    channel.listen('message-edited', editedListener);

                    // Store everything needed for cleanup
                    dispatch(updateSubChannel({
                        channel,
                        listener,
                        connection: pieSocketConn,
                        roomId: jwtObj.roomId,
                    }))
                });
            }
        });
    }, [businessIdToPhoneMap, currentPractice, dispatch, whatsappJwtTokens]);

    React.useEffect(() => {
        // Loading Merge Tags in First Load for use accross application
        if (currentPractice) {
            dispatch(fetchMergeTagKeys());
            dispatch(fetchMergeTagValueForPractice());
            dispatch(fetchPracticeUsers());
            dispatch(fetchMergeTagValueForUser());
            dispatch(resetChatData());
            dispatch(initializeWhatsapp());
            dispatch(getAllTagItem(currentPractice, 30, 0));
            dispatch(selectLeads({ leadId: [], type: '', loading: false }));
            dispatch(getPracticeTablePreferences(currentPractice));
            requestAndRegisterFCMToken(currentPractice);
            listenForForegroundMessages(showCustomNotification);
        }
    }, [dispatch, currentPractice]);

    // Prefetch all Google Reviews data when dashboard loads
    // This loads accounts, locations, and all review data so the page opens instantly
    React.useEffect(() => {
        if (currentPractice) {
            dispatch(prefetchGoogleReviewsOnDashboardLoad(currentPractice));
        }
    }, [dispatch, currentPractice]);

    // Global keyboard shortcut for command palette (⌘K / Ctrl+K)
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setCommandPaletteOpen(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);


    // P2-2 I1: single top-level mount of TaskUndoSnackbarHost so every V2
    // task surface (Dashboard / TaskDashboard / LeadTaskList) shares one
    // window-event listener. popUndo's drain semantics already keep
    // multiple hosts safe; this removes the listener fan-out + ensures the
    // Snackbar appears regardless of which V2 surface triggered completion.
    const renderUndoHost = currentPractice
        ? <TaskUndoSnackbarHost practiceId={currentPractice} />
        : null;

    if (horizontalLayout) {
        return (
            <>
                <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
                <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
                <CreateLeadDialogWrapper />
                {renderUndoHost}

                {isDesktop ? <NavbarHorizontal /> : renderNavVertical}

                <Main>
                    <Outlet />
                </Main>
            </>
        );
    }

    if (isNavMini) {
        return (
            <>
                <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
                <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
                <CreateLeadDialogWrapper />
                {renderUndoHost}

                <Box
                    sx={{
                        display: { lg: 'flex' },
                        minHeight: { lg: 1 },
                    }}
                >
                    {isDesktop ? <NavMini /> : renderNavVertical}

                    <Main>
                        <Outlet />
                    </Main>
                </Box>
            </>
        );
    }

    return (
        <>
            <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
            <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
            <CreateLeadDialogWrapper />
            <EnableNotificationsDialog />
            {renderUndoHost}
            <Box
                sx={{
                    display: { lg: 'flex' },
                    minHeight: { lg: 1 },
                }}
            >
                {renderNavVertical}

                <Main>
                    <Outlet />
                </Main>
            </Box>
        </>
    );
}
